import classnames from 'classnames/bind'
import React, {
  ReactNode,
  RefObject,
  SelectHTMLAttributes,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react'

import { useMeasureObserver } from '@unlikelystudio/react-hooks'

import { useStyle } from '~/providers/StyleProvider'

import { ReactComponent as DropdownIcon } from './arrow.svg'
import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface Option {
  label: ReactNode
  value: any
}

export interface SelectProps extends SelectHTMLAttributes<any> {
  className?: string
  forwardRef?: any
  options?: Option[]
  layout?: 'default' | 'light' | 'big' | 'rounded'
  textPreset?: string
  customLabel?: string
}

function Select({
  className,
  layout,
  options,
  forwardRef,
  onChange,
  customLabel,
  required,
  textPreset = 'text-paragraph',
  defaultValue,
  name,
  ...selectProps
}: SelectProps) {
  const findIndexFromValue = (value: any) => {
    return options?.findIndex((option) => option.value == value)
  }

  const [valueIndex, setValueIndex] = useState(
    defaultValue ? findIndexFromValue(defaultValue) : 0,
  )

  const shadowTextRef = useRef(null)
  const shadowTextBounds = useMeasureObserver(shadowTextRef)

  const selectTextStyle = useStyle({ textPreset })
  const textErrorStyle = useStyle({ textPreset: 'text-label' })

  const customOnChange = (e) => {
    const value = e.target.value
    setValueIndex(findIndexFromValue(value))
    onChange?.(e)
  }

  const validations = {
    required,
  }

  return (
    <div className={cx(className, css.Select, layout)}>
      <DropdownIcon width={10} height={10} className={css.arrow} />
      <div ref={shadowTextRef} className={cx(css.shadowText, selectTextStyle)}>
        {options[valueIndex]?.label?.toString()}
      </div>
      <select
        className={cx(css.select, selectTextStyle)}
        onChange={customOnChange}
        style={
          layout !== 'rounded'
            ? {
                width: shadowTextBounds.width,
              }
            : {}
        }
        name={name}
        defaultValue={defaultValue}
        {...selectProps}>
        {options?.map(({ value, label }, index) => {
          return (
            <option
              className={cx(css.option, selectTextStyle)}
              key={`${value}-${index}`}
              value={value}>
              {`${
                customLabel && index === valueIndex ? `${customLabel} ` : ''
              }${label}`}
            </option>
          )
        })}
      </select>
    </div>
  )
}

Select.defaultProps = {
  layout: 'default',
}

export default Select
